import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, TableHead, TableRow, TableCell, TableBody, Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';

import { DialogUI, InputUI, ButtonUI } from '../../Interface';
import { DocumentType } from '../../../config/DocumentType';
import productionService from '../../../service/production.service';
import { cleanValues } from '../../../utils/helper';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import ProductionBatchOutDialog from './ProductionBatchOutDialog';
import { InvManagementMethod } from '../../../config/itemGroup';

const ProductionIssueDialog = ({ open, onClose, selectedValues, setSelectedValues, setRefetchTable }) => {
  const theme = useTheme();
  const ui = theme.palette.ui;

  const [docLines, setDocLines] = useState([]);
  const [batchLine, setBatchLine] = useState(null);

  const defaultBatchLine = {
    id: null,
    batchSn: '',
    lot: '',
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    quantity: '',
  };

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    docLineBatchSn: [],
  };

  const defaultValues = {
    docTypeId: DocumentType.IssueForProduction,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: selectedValues.whsCode,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const setQty = () => {
    if (!selectedValues?.productionItem) {
      return addNotification({
        content: 'Nincs rögzítve tétel!',
        type: NotificationType.WARNING,
      });
    }

    if (!selectedValues?.whsCode) {
      return addNotification({
        content: 'Nincs rögzítve fej szintű raktár!',
        type: NotificationType.WARNING,
      });
    }
    const emptyLineWhs = selectedValues.productionItem.find((itm) => !itm.whsCode);
    if (emptyLineWhs) {
      addNotification({
        content: 'Nincs rögzítve sor szintű raktár!',
        type: NotificationType.WARNING,
      });
    }

    const newDocLines = selectedValues.productionItem
      .filter((itm) => !itm.secondaryProduct)
      .map((itm) => {
        return {
          ...defaultDocumentLine,
          id: itm.id,
          itemCode: itm.itemCode,
          itemName: itm.item?.itemName,
          qty: Math.abs(itm.quantity || ''),
          whsCode: itm.whsCode,
          weight: itm.weight || '',
          quantityUnitId: itm.item?.quantityUnit?.id,
          quantityUnitCode: itm.item?.quantityUnit?.code,
          quantityUnitName: itm.item?.quantityUnit?.name,
          baseId: selectedValues.id,
          baseLine: itm.id,
          baseDocnum: selectedValues.workingNumber,
          baseDocType: null,
        };
      });

    setDocLines(newDocLines);
  };

  useEffect(() => {
    if (open) {
      setQty();
    } else {
      setDocLines([]);
    }
  }, [open]);

  const handleQuantityChange = (id, value) => {
    setDocLines((prev) => prev.map((line) => (line.id === id ? { ...line, qty: parseFloat(value) } : line)));
  };

  const handleCloseBatchDialog = () => {
    setBatchLine(null);
  };

  const handlePostDocument = () => {
    const document = {
      ...cleanValues(defaultValues),
      docLine: docLines
        .filter((l) => l.qty > 0)
        .map((line) => ({
          ...cleanValues(line),
          docLineBatchSn: line.docLineBatchSn.map((batch) => ({
            ...cleanValues(batch),
          })),
        })),
    };

    productionService.createProductionIssue(document, selectedValues.id).then((data) => {
      onClose();
      setRefetchTable(true);
      setSelectedValues(data);
    });
  };

  const handleBatchSave = (dl) => {
    setDocLines((prev) => prev.map((line) => (line.id === batchLine.id ? { ...line, docLineBatchSn: dl } : line)));
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent={'Kiadás'} maxWidth="lg" fullWidth>
      <Table style={{ marginTop: 16 }}>
        <TableHead sx={{ background: ui.tableHeader }}>
          <TableRow>
            <TableCell>Cikkszám</TableCell>
            <TableCell>Megnevezés</TableCell>
            <TableCell>Mennyiség</TableCell>
            <TableCell>Me.</TableCell>
            <TableCell>Sarzs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {docLines.map((itm, idx) => (
            <TableRow key={itm.id}>
              <TableCell>{itm.itemCode}</TableCell>
              <TableCell>{itm.itemName}</TableCell>
              <TableCell style={{ maxWidth: 120 }}>
                <InputUI
                  type="number"
                  value={itm.qty}
                  onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                  inputProps={{ min: 0 }}
                  size="small"
                />
              </TableCell>
              <TableCell>{itm.quantityUnitCode}</TableCell>
              <TableCell>
                {(() => {
                  const item = selectedValues?.productionItem?.find((i) => i.itemCode === itm.itemCode);
                  if (item && item.item?.invManagementMethod === InvManagementMethod.BATCH) {
                    return (
                      <IconButton
                        onClick={() => {
                          setBatchLine({ ...itm, selectedLineIndex: idx });
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    );
                  }
                  return null;
                })()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ProductionBatchOutDialog
        open={!!batchLine}
        onClose={handleCloseBatchDialog}
        selectedLine={batchLine}
        whsCode={selectedValues?.whsCode}
        defaultBatchLine={defaultBatchLine}
        onSave={(dl) => handleBatchSave(dl)}
      />

      <Box sx={{ px: 4, mt: 8, display: 'flex', justifyContent: 'center', gap: 3 }}>
        <ButtonUI
          text={'Könyvelés'}
          className="bg-success"
          type="submit"
          onClick={() => handlePostDocument()}
        ></ButtonUI>
        <ButtonUI
          text="Mégse"
          className="bg-"
          color="blue-gray"
          type="button"
          variant="gradient"
          onClick={onClose}
        ></ButtonUI>
      </Box>
    </DialogUI>
  );
};

export default ProductionIssueDialog;
